import React, { useState, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { Calendar } from '../components/PrimeCalendar';
import { OverlayPanel } from 'primereact/overlaypanel';
import { DateTime } from 'luxon';
import { uiT } from './uiTranslate';

/** Min. šírka stĺpca s `dateUserHeaderFilter` (text + rozsah dátumov v hlavičke); zjednotené naprieč servisnými zoznamami a mntn001. */
export const DATE_USER_HEADER_FILTER_COL_MIN_WIDTH = 250;

const RANGE_PREFIX = 'range:';

function formatRangeDisplay(fromStr, toStr, dateFormat) {
  if (!fromStr || !toStr) return '';
  const from = DateTime.fromFormat(fromStr, 'yyyy-MM-dd');
  const to = DateTime.fromFormat(toStr, 'yyyy-MM-dd');
  if (!from.isValid || !to.isValid) return `${fromStr} - ${toStr}`;
  return `${from.toFormat(dateFormat)} - ${to.toFormat(dateFormat)}`;
}

/**
 * Header filter: text (user meno alebo dátum/substring) + ikona kalendára pre výber rozsahu (od-do).
 * Hodnota: buď plain text (LIKE), alebo "range:YYYY-MM-DD:YYYY-MM-DD" (whereBetween).
 * editorParams: { dateFormat, dateTimeFormat } – dateFormat by mal byť z getDateFormat(module_settings) (Jednotky).
 */
export function dateUserHeaderFilter(cell, onRendered, success, cancel, editorParams) {
  const mount = document.createElement('div');
  mount.style.cssText = 'display:flex;align-items:center;position:relative;width:100%;max-width:100%;min-width:0;padding:0 2px;box-sizing:border-box;overflow:hidden;';
  const dateFormat = editorParams?.dateFormat ?? 'dd.MM.yyyy';
  const dateTimeFormat = editorParams?.dateTimeFormat ?? `${dateFormat} HH:mm`;

  let root = null;
  onRendered(() => {
    root = createRoot(mount);
    const column = cell.getColumn?.();
    const raw = column?.getHeaderFilterValue?.() ?? cell.getValue?.();
    const initial = raw === undefined || raw === null ? '' : String(raw).trim();

    const FilterUI = () => {
      /** Musí byť vo vnútri React komponentu — useRef v onRendered by porušil Rules of Hooks. */
      const committedRef = useRef(initial);
      const [inputVal, setInputVal] = useState(() => {
        if (!initial) return '';
        if (initial.startsWith(RANGE_PREFIX)) {
          const parts = initial.slice(RANGE_PREFIX.length).split(':');
          if (parts.length >= 2 && parts[0] && parts[1]) {
            return formatRangeDisplay(parts[0], parts[1], dateFormat);
          }
        }
        return initial;
      });
      const [rawValue, setRawValue] = useState(initial);
      const overlayRef = useRef(null);
      const [rangeValue, setRangeValue] = useState(null);

      const notify = useCallback((value) => {
        const v = value === undefined || value === null ? '' : String(value);
        committedRef.current = v;
        setRawValue(v);
        success(v);
      }, [success]);

      const handleInputChange = (e) => {
        const v = e.target.value ?? '';
        setInputVal(v);
        if (String(v).trim() === '') {
          notify('');
        }
      };

      const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          const v = String(inputVal).trim();
          setTimeout(() => notify(v), 0);
        }
      };

      const handleCalendarSelect = (e) => {
        const v = e.value;
        if (!Array.isArray(v)) return;
        const from = v[0] ?? null;
        const to = v[1] ?? null;
        if (from && to) {
          const fromStr = DateTime.fromJSDate(from).toFormat('yyyy-MM-dd');
          const toStr = DateTime.fromJSDate(to).toFormat('yyyy-MM-dd');
          const rangeStr = `${RANGE_PREFIX}${fromStr}:${toStr}`;
          setInputVal(formatRangeDisplay(fromStr, toStr, dateFormat));
          overlayRef.current?.hide();
          setTimeout(() => notify(rangeStr), 0);
        } else {
          const next = from ? [new Date(from.getTime()), to ? new Date(to.getTime()) : null] : null;
          flushSync(() => setRangeValue(next));
        }
      };

      const openCalendar = (event) => {
        setRangeValue(null);
        overlayRef.current?.toggle(event);
      };
      const clearFilter = () => {
        setInputVal('');
        setRangeValue(null);
        overlayRef.current?.hide();
        setTimeout(() => notify(''), 0);
      };
      const hasActiveFilter = String(rawValue || inputVal || '').trim() !== '';

      return (
        <>
          <div className="flex items-center relative min-w-0 flex-1 w-full max-w-full" style={{ overflow: 'hidden' }}>
            <input
              type="text"
              value={inputVal}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              className="w-full min-w-0 py-1 pl-1 pr-7 text-sm border border-surface-300 rounded box-border"
              style={{ maxWidth: '100%', boxSizing: 'border-box' }}
            />
            <button
              type="button"
              onClick={hasActiveFilter ? clearFilter : openCalendar}
              className="absolute right-0.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-surface-100 text-surface-600 shrink-0"
              title={hasActiveFilter ? uiT('DateUserFilter.Clear') : uiT('DateUserFilter.Range')}
              aria-label={hasActiveFilter ? uiT('DateUserFilter.Clear') : uiT('DateUserFilter.Calendar')}
            >
              <i className={hasActiveFilter ? 'pi pi-times' : 'pi pi-calendar'} style={{ fontSize: '0.9rem' }} />
            </button>
          </div>
          <OverlayPanel ref={overlayRef} appendTo={typeof document !== 'undefined' ? document.body : undefined}>
            <div className="p-2" onClick={(e) => e.stopPropagation()}>
              <label className="block text-sm font-medium mb-2">{uiT('DateUserFilter.RangeLabel')}</label>
              <Calendar
                inline
                value={rangeValue ? [rangeValue[0], rangeValue[1] ?? null] : null}
                onChange={handleCalendarSelect}
                selectionMode="range"
                dateFormat="dd.mm.yy"
                locale="sk"
                hideOnRangeSelection
              />
            </div>
          </OverlayPanel>
        </>
      );
    };

    root.render(<FilterUI />);
  });

  return mount;
}
